/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { Form, DateInput, Tooltip } from '@mairistem/composants';
import { Section } from '@mairistem/administres-utils';

import { DATE_FORMAT } from '@/constants';
import { createTimeInputHandler } from '@/utils';

import { ChampLieu } from '@/components/Common';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit. Sans lui, `dayjs(valeur, DATE_FORMAT)` rend « Invalid Date » —
// silencieusement à l'écran, et de façon reproductible en test isolé.
dayjs.extend(customParseFormat);

interface InformationsEvenementMariageProps {
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (
    event: React.SyntheticEvent | null,
    payload: { name: string; value: unknown },
  ) => void;
}

/**
 * Bloc « INFORMATIONS SUR LE MARIAGE » : date et heure de la célébration, et
 * lieu où elle a eu lieu.
 *
 * ⚠️ Ce lieu n'est PAS celui où l'acte est dressé. `ec_actm` porte les deux, sur
 * deux lignes `ec_acte_adresse` distinctes que le fonds repris ne confond jamais
 * (« OLLIOULES » pour l'acte, « MAIRIE D'OLLIOULES » pour la célébration).
 * `buildActeMariagePayload` les envoie séparément.
 *
 * Une commune et non une adresse complète : les 2809 lieux de célébration du
 * fonds repris n'ont aucune voie renseignée.
 */
const InformationsEvenementMariage = (
  props: InformationsEvenementMariageProps,
) => {
  const { data, errors = {}, onChange } = props;

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: 'dateMariage',
        value: _.isNil(date) ? null : dayjs(date).format(DATE_FORMAT),
      });
    },
    [onChange],
  );

  const handleTimeInput = React.useMemo(
    () => createTimeInputHandler(onChange),
    [onChange],
  );

  /**
   * Validité de la commune à la date du MARIAGE, et non à celle de l'acte : les
   * deux peuvent être éloignées, et des communes ont fusionné entre-temps.
   */
  const dateValiditeLieu = !_.isEmpty(data?.dateMariage)
    ? dayjs(data.dateMariage, DATE_FORMAT).format('YYYY-MM-DD')
    : dayjs().format('YYYY-MM-DD');

  return (
    <Section title="INFORMATIONS SUR LE MARIAGE" height="auto">
      {/* @ts-ignore */}
      <Form style={{ padding: '8px 14px' }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: '1em',
          }}
        >
          <Form.Field required error={!_.isNil(errors?.dateMariage)}>
            <label>Marié le</label>
            <Tooltip
              trigger={
                <DateInput
                  value={
                    data?.dateMariage
                      ? dayjs(data.dateMariage, DATE_FORMAT).toDate()
                      : undefined
                  }
                  onChange={handleDateChange}
                />
              }
              type={errors?.dateMariage ? 'error' : ''}
              message={errors?.dateMariage}
            />
          </Form.Field>
          <Form.Group style={{ alignItems: 'flex-end' }}>
            <Tooltip
              trigger={
                <Form.Input
                  label="à"
                  placeholder="hh"
                  name="heureMariage"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.heureMariage)}
                  value={
                    !_.isNil(data?.heureMariage)
                      ? String(data.heureMariage).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.heureMariage ? 'error' : ''}
              message={errors?.heureMariage}
            />
            <span style={{ alignSelf: 'flex-end', lineHeight: '38px' }}>:</span>
            <Tooltip
              trigger={
                <Form.Input
                  placeholder="mm"
                  name="minuteMariage"
                  style={{ width: 48 }}
                  error={!_.isNil(errors?.minuteMariage)}
                  value={
                    !_.isNil(data?.minuteMariage)
                      ? String(data.minuteMariage).padStart(2, '0')
                      : ''
                  }
                  onChange={handleTimeInput}
                />
              }
              type={errors?.minuteMariage ? 'error' : ''}
              message={errors?.minuteMariage}
            />
          </Form.Group>
        </div>
        <ChampLieu
          data={data}
          errors={errors}
          onChange={onChange}
          name="adresseMariage"
          title="Lieu du mariage"
          dateValidite={dateValiditeLieu}
        />
      </Form>
    </Section>
  );
};

export default InformationsEvenementMariage;
